import { BETA_JRCDragAndDrop } from 'components/Beta/BETA_JRCDragAndDrop/BETA_JRCDragAndDrop';
import { JRCIconButton } from 'components/JRCIconButton/JRCIconButton';
import { JRCTypography } from 'components/JRCTypography/JRCTypography';
import { Utils } from 'index';
import {
    CombinedWidgetContent,
    jFileLittle,
    WidgetCheckListContent,
    WidgetCheckListContentArr,
} from 'jamespot-user-api';
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { JRCWidgetCheckListEditorItemLink } from './JRCWidgetCheckListEditorItemLink';
import { JRCWidgetCheckListItem } from './JRCWidgetCheckListItem';

import { EditorInputToggle } from 'components/Editors/components/EditorInputToggle';

const Column = styled.div`
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
`;

const Row = styled.div`
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    cursor: pointer;
`;

const RowSB = styled(Row)`
    justify-content: space-between;
`;

const ColumnWithDraggable = styled(Column)`
    > div {
        width: 100%;
    }
`;

export const FormItemContainer = styled.div<{ border?: boolean }>`
    width: 100%;
    padding-bottom: 12px;
    border-bottom: ${(props) => (props.border ? `1px solid ${props.theme.color.grey1}` : 0)};
`;

export const CSSRowLinkWrapper = styled.div<{ border?: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border: ${(props) => (props.border ? `1px solid ${props.theme.color.grey2}` : 'none')};
    min-height: 24px;
    border-radius: 4px;
    padding: 4px 8px;
`;

export const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

type JRCWidgetCheckListEditorProps = {
    token?: string;
    uniqid: string;
    content: WidgetCheckListContent;
    inplace?: boolean;
    onChangeContent: (
        uniqid: string,
        content: {
            arr: WidgetCheckListContentArr[];
        },
    ) => void;
    onChangeWrapper: ({ uniqid, title }: { uniqid: string; title: string }) => void;
    onSave?: (uniqid: string, content: Partial<CombinedWidgetContent>) => void;
    handleChange: (content: Partial<WidgetCheckListContent>) => void;
};
export const JRCWidgetCheckListEditor = ({
    token,
    uniqid,
    content,
    inplace = false,
    onChangeContent,
    onChangeWrapper,
    handleChange,
}: JRCWidgetCheckListEditorProps) => {
    const intl = useIntl();
    const [openUriModal, setOpenUriModal] = useState(false);
    const [currentModalIdx, setCurrentModalIdx] = useState<number | null>(null);
    const [lastInputIndex, setLastInputIndex] = useState(0); // State to hold the index of the last input
    const inputRefs = useRef<RefObject<HTMLInputElement>[]>([]); // Ref to hold all input refs

    const addInput = useCallback(() => {
        setLastInputIndex(content?.arr?.length ?? 0);
    }, [content?.arr?.length]);

    const handleNewItem = useCallback(() => {
        if (!Array.isArray(content.arr)) return;

        const arrayToChange = [...content.arr, { title: '', value: false }];

        handleChange({ arr: arrayToChange });
        addInput();
        inputRefs.current.push(React.createRef<HTMLInputElement>());
    }, [addInput, handleChange, content.arr]);

    const handleTitleChange = useCallback(
        (title: string) => {
            onChangeWrapper({ uniqid: uniqid, title });
        },
        [onChangeWrapper, uniqid],
    );

    const handleItemChange = (idx: number, item: WidgetCheckListContentArr) => {
        const newItems = content?.arr?.map((oldItem, index) => {
            if (index !== idx) return oldItem;

            return { ...item };
        });

        if (!newItems) return;
        const newContent = {
            arr: newItems,
        };
        onChangeContent(uniqid, newContent);
    };

    const handleRemoveItem = (idx: number) => {
        const tmp = [...(content?.arr ?? [])];

        tmp.splice(idx, 1);

        const newContent = {
            arr: tmp,
        };
        onChangeContent(uniqid, newContent);
    };

    const handleAddUri = (idx: number) => {
        setCurrentModalIdx(idx);
        setOpenUriModal(true);
    };

    const handleRemoveUri = (idxUri: number, idxWidget: number) => {
        const newContent = {
            arr: [
                ...(content?.arr?.map((item, index) => {
                    if (index !== idxWidget) return item;
                    const links = item.links?.filter((_, index) => index !== idxUri);
                    return { ...item, links };
                }) || []),
            ],
        };
        onChangeContent(uniqid, newContent);
    };

    const handleResetModal = () => {
        setOpenUriModal(false);
        setCurrentModalIdx(null);
    };

    const handleRemoveImage = (idx: number) => {
        if (!Array.isArray(content.arr)) return;

        const arrayToChange = content.arr.map((entry, index) => {
            if (index === idx) {
                const tmp = { ...entry };
                delete tmp.id;
                delete tmp.file;
                return tmp;
            } else {
                return entry;
            }
        });

        handleChange({ arr: arrayToChange });
    };

    const handleSwapItems = (content: { arr: WidgetCheckListContentArr[] }) => {
        onChangeContent(uniqid, content);
    };

    const handleAddNewImage = (idx: number, file: jFileLittle) => {
        if (!Array.isArray(content.arr)) return;

        const arrayToChange = content.arr.map((el, index) => (index === idx ? { ...el, id: file.id, file } : el));

        handleChange({ arr: arrayToChange });
    };

    useEffect(() => {
        if (content.arr?.length === 0) {
            handleNewItem();
            handleTitleChange('');
            addInput();
        }
    }, [handleNewItem, content.arr, handleTitleChange, addInput]);

    useEffect(() => {
        if (inputRefs.current[lastInputIndex]?.current) {
            inputRefs.current[lastInputIndex].current?.focus();
        }
    }, [lastInputIndex]);

    return (
        <Column>
            <FormContainer>
                {content && content.arr && (
                    <FormItemContainer border={content.arr.length > 0 ? true : false}>
                        <ColumnWithDraggable>
                            <BETA_JRCDragAndDrop
                                onDrag={(dragIndex, hoverIndex) => {
                                    if (content.arr === undefined) return;
                                    const newValue = Utils.array.arraySwapElements(content.arr, dragIndex, hoverIndex);
                                    const newContent = {
                                        arr: newValue,
                                    };
                                    handleSwapItems(newContent);
                                }}
                                elms={content?.arr ? content?.arr : []}
                                renderEl={(item: WidgetCheckListContentArr, index: number) => {
                                    return (
                                        <JRCWidgetCheckListItem
                                            token={token}
                                            key={index}
                                            index={index}
                                            item={item}
                                            setItems={handleItemChange}
                                            removeItem={handleRemoveItem}
                                            handleAddUri={handleAddUri}
                                            handleRemoveUri={handleRemoveUri}
                                            handleAddImageWidget={handleAddNewImage}
                                            handleRemoveImage={handleRemoveImage}
                                            handleNewItem={handleNewItem}
                                            length={content?.arr?.length ?? 0}
                                            inputRef={inputRefs.current[index]}
                                        />
                                    );
                                }}
                            />
                        </ColumnWithDraggable>
                    </FormItemContainer>
                )}
                <FormItemContainer>
                    <RowSB>
                        <Row onClick={handleNewItem}>
                            <JRCIconButton
                                icon="icon-plus"
                                title={intl.formatMessage({ id: 'BUTTON_Title_Add_New_Task' })}
                            />
                            <JRCTypography size="l">
                                {intl.formatMessage({ id: 'COMMENT_WIDGET_CHECKLIST_Add_New_Task' })}
                            </JRCTypography>
                        </Row>
                    </RowSB>
                </FormItemContainer>
                {!inplace && (
                    <>
                        <h3>{intl.formatMessage({ id: 'WIDGET_Collaboration' })}</h3>
                        <EditorInputToggle
                            initialValue={content.edit ?? false}
                            onChange={(edit) => handleChange({ ...content, edit })}
                            options={[
                                {
                                    label: intl.formatMessage({ id: 'WIDGET_Check_List_Help' }),
                                    value: 1,
                                },
                            ]}
                        />
                    </>
                )}
            </FormContainer>
            {openUriModal && (
                <JRCWidgetCheckListEditorItemLink
                    content={content}
                    idx={currentModalIdx ?? -1}
                    handleChangeContent={(content) => {
                        if (content.arr) {
                            onChangeContent(uniqid, { arr: content.arr });
                        }
                        handleResetModal();
                    }}
                    closeHandler={handleResetModal}
                />
            )}
        </Column>
    );
};
